import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { workspaceHomePath } from '../../auth/workspaces';
import { UmbLogo } from '../../components/layout/UmbLogo';

function roleLabel(orgType) {
  const value = String(orgType || '').toLowerCase();
  if (value === 'originator') return 'Originator';
  if (value === 'provider') return 'Provider';
  if (value === 'custodian') return 'Custodian';
  if (value === 'investor') return 'Investor';
  return orgType;
}

export function WorkspacePickerPage() {
  const navigate = useNavigate();
  const {
    jwtMode,
    session,
    memberships,
    pendingLogin,
    selectOrganization,
    setAuthError,
    authError,
  } = useAuth();
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!jwtMode) return;
    if (memberships.length === 1 && pendingLogin) {
      selectOrganization(memberships[0].organization_id)
        .then((next) => navigate(workspaceHomePath(next.workspaceId), { replace: true }))
        .catch((err) => setAuthError(err.message || 'Could not open workspace'));
    }
  }, [jwtMode, memberships, pendingLogin, selectOrganization, navigate, setAuthError]);

  if (!jwtMode) {
    return <Navigate to="/signin" replace />;
  }

  if (session) {
    return <Navigate to={workspaceHomePath(session.workspaceId)} replace />;
  }

  if (!pendingLogin) {
    return <Navigate to="/signin" replace />;
  }

  async function handleSelect(membership) {
    setBusyId(membership.organization_id);
    setAuthError(null);
    try {
      const next = await selectOrganization(membership.organization_id);
      navigate(workspaceHomePath(next.workspaceId));
    } catch (err) {
      setAuthError(err.message || 'Could not open workspace');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-eorig-surface px-4 py-10 font-sans text-eorig-ink">
      <div className="w-full max-w-xl">
        <div className="rounded-2xl border border-eorig-border bg-white p-6 shadow-eorig-card sm:p-8">
          <UmbLogo />
          <h1 className="mt-6 text-2xl font-semibold tracking-tight">Select workspace</h1>
          <p className="mt-2 text-sm text-eorig-muted">
            Signed in as {pendingLogin.email}.{' '}
            {pendingLogin.workspaceType
              ? `You hold ${roleLabel(pendingLogin.workspaceType)} access in more than one organization — choose the one to work in.`
              : 'Choose the organization to work in.'}
          </p>
          {authError ? (
            <p className="mt-4 text-sm text-red-600" role="alert">
              {authError}
            </p>
          ) : null}
          <div className="mt-6 space-y-3">
            {memberships.map((membership) => (
              <button
                key={membership.organization_id}
                type="button"
                disabled={Boolean(busyId)}
                onClick={() => handleSelect(membership)}
                className="flex w-full flex-col rounded-2xl border border-eorig-border bg-eorig-surface px-4 py-4 text-left transition hover:border-eorig-cyan/40 hover:bg-white disabled:opacity-60"
              >
                <span className="text-sm font-semibold text-eorig-ink">
                  {membership.organization_name}
                </span>
                <span className="mt-1 text-xs uppercase tracking-wide text-eorig-muted">
                  {roleLabel(membership.org_type)} · {membership.role}
                </span>
                {busyId === membership.organization_id ? (
                  <span className="mt-2 text-xs text-eorig-muted">Opening…</span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
